// ========================================
// 炎属性カードの固有効果
// ========================================

import {
  getPlayerContext,
  millDeck,
  conditionalDamage,
  searchCard,
  reviveFromGraveyard,
  modifyAttack,
  applyAwakening,
  selectAndApplyAwakening,
} from '../effectHelpers';
import { statusEffectEngine, STATUS_EFFECT_TYPES } from '../statusEffects';
import { hasCategory } from '../../utils/helpers';
import type { CardEffectRegistry } from './index';
import type { MonsterInstance, CardWithId, CardData } from '../../types';

/**
 * 炎属性カードの固有効果
 */
export const fireCardEffects: CardEffectRegistry = {
  /**
   * C0000021: フレア・ドラゴン
   * 基本技：自身の攻撃力の半分のダメージを相手モンスター1体に与える
   */
  C0000021: (skillText, context) => {
    const { addLog, monsterIndex, setPendingMonsterTarget } = context;
    const { myField, opponentField, setOpponentField } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      const monster = myField[monsterIndex];
      if (!monster) {
        addLog('モンスターが存在しません', 'damage');
        return false;
      }

      // ダメージ量 = 攻撃力の半分
      const damage = Math.floor((monster.attack ?? 0) / 2);

      // 相手フィールドのモンスターをチェック
      const validTargets = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(t => t.monster !== null);

      if (validTargets.length === 0) {
        addLog('相手フィールドにモンスターがいません', 'info');
        return true; // 対象がいなくても技の発動自体は成功として扱う
      }

      // 1体のみの場合は自動選択
      if (validTargets.length === 1) {
        const target = validTargets[0];
        setOpponentField(prev => prev.map((m, idx) => {
          if (idx === target.index && m) {
            const newHp = m.currentHp - damage;
            addLog(`${monster.name}の基本技: ${m.name}に${damage}ダメージ！`, 'damage');
            if (newHp <= 0) {
              addLog(`${m.name}は破壊された！`, 'damage');
              return null;
            }
            return { ...m, currentHp: newHp };
          }
          return m;
        }));
        return true;
      }

      // 複数の場合はターゲット選択UI
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: `${damage}ダメージを与える相手モンスターを選択`,
          targetPlayer: 'opponent',
          callback: (targetIndex: number) => {
            setOpponentField(prev => prev.map((m, idx) => {
              if (idx === targetIndex && m) {
                const newHp = m.currentHp - damage;
                addLog(`${monster.name}の基本技: ${m.name}に${damage}ダメージ！`, 'damage');
                if (newHp <= 0) {
                  addLog(`${m.name}は破壊された！`, 'damage');
                  return null;
                }
                return { ...m, currentHp: newHp };
              }
              return m;
            }));
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      // フォールバック: 最初のターゲットを選択
      const target = validTargets[0];
      setOpponentField(prev => prev.map((m, idx) => {
        if (idx === target.index && m) {
          const newHp = m.currentHp - damage;
          addLog(`${monster.name}の基本技: ${m.name}に${damage}ダメージ！`, 'damage');
          if (newHp <= 0) {
            addLog(`${m.name}は破壊された！`, 'damage');
            return null;
          }
          return { ...m, currentHp: newHp };
        }
        return m;
      }));
      return true;
    }
    return false;
  },

  /**
   * C0000022: 火竜の吐息
   * 【刹那詠唱】相手のモンスター1体に1000ダメージを与える。相手プレイヤーに500ダメージを与える。
   */
  C0000022: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    // 相手フィールドのモンスターをチェック
    const validTargets = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(t => t.monster !== null);

    // 相手プレイヤーに500ダメージは必ず与える
    conditionalDamage(context, 500, 'opponent');

    if (validTargets.length === 0) {
      addLog('相手フィールドにモンスターがいないため、モンスターへのダメージはなし', 'info');
      return true;
    }

    // 1体のみの場合は自動選択
    if (validTargets.length === 1) {
      const target = validTargets[0];
      setOpponentField(prev => {
        const newField = [...prev];
        const m = newField[target.index];
        if (m) {
          const newHp = m.currentHp - 1000;
          addLog(`火竜の吐息: ${m.name}に1000ダメージ！`, 'damage');
          if (newHp <= 0) {
            addLog(`${m.name}は破壊された！`, 'damage');
            setOpponentGraveyard(prev => [...prev, m]);
            newField[target.index] = null;
          } else {
            newField[target.index] = { ...m, currentHp: newHp };
          }
        }
        return newField;
      });
      return true;
    }

    // 複数の場合はターゲット選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '1000ダメージを与える相手モンスターを選択',
        targetPlayer: 'opponent',
        callback: (targetIndex: number) => {
          setOpponentField(prev => {
            const newField = [...prev];
            const m = newField[targetIndex];
            if (m) {
              const newHp = m.currentHp - 1000;
              addLog(`火竜の吐息: ${m.name}に1000ダメージ！`, 'damage');
              if (newHp <= 0) {
                addLog(`${m.name}は破壊された！`, 'damage');
                setOpponentGraveyard(prev => [...prev, m]);
                newField[targetIndex] = null;
              } else {
                newField[targetIndex] = { ...m, currentHp: newHp };
              }
            }
            return newField;
          });
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // フォールバック: 最初のターゲットを選択
    const target = validTargets[0];
    setOpponentField(prev => {
      const newField = [...prev];
      const m = newField[target.index];
      if (m) {
        const newHp = m.currentHp - 1000;
        addLog(`火竜の吐息: ${m.name}に1000ダメージ！`, 'damage');
        if (newHp <= 0) {
          addLog(`${m.name}は破壊された！`, 'damage');
          setOpponentGraveyard(prev => [...prev, m]);
          newField[target.index] = null;
        } else {
          newField[target.index] = { ...m, currentHp: newHp };
        }
      }
      return newField;
    });
    return true;
  },

  /**
   * C0000024: 紅蓮爆発
   * 手札を全て捨てる。自分の場にいるモンスターの攻撃力を倍にする。
   */
  C0000024: (skillText, context) => {
    const { addLog } = context;
    const { myHand, setMyHand, setMyGraveyard, myField, setMyField } = getPlayerContext(context);

    // 手札を全て墓地に送る
    if (myHand.length > 0) {
      const discardedCards = [...myHand];
      setMyGraveyard(prev => [...prev, ...discardedCards]);
      setMyHand([]);
      addLog(`紅蓮爆発: 手札${discardedCards.length}枚を全て捨てた！`, 'info');
    } else {
      addLog('紅蓮爆発: 手札がない状態で発動', 'info');
    }

    // 自分の場の全モンスターの攻撃力を倍にする
    let buffedCount = 0;
    setMyField(prev => prev.map(m => {
      if (m) {
        const newAttack = (m.attack ?? 0) * 2;
        const newCurrentAttack = (m.currentAttack ?? m.attack ?? 0) * 2;
        buffedCount++;
        return { ...m, attack: newAttack, currentAttack: newCurrentAttack };
      }
      return m;
    }));

    if (buffedCount > 0) {
      addLog(`紅蓮爆発: 自分のモンスター${buffedCount}体の攻撃力が倍になった！`, 'damage');
    }

    return true;
  },

  /**
   * C0000028: 炎竜母フレイマ
   * 【召喚時】墓地の［ドラゴン］モンスター1体を攻撃力半減で場に戻す
   * 【常時】自分の［ドラゴン］モンスターは効果でダメージを受けない
   * 基本技：場の［ドラゴン］モンスター1体のHPを500回復する
   * 上級技：墓地の［ドラゴン］モンスター1体を攻撃力半減で場に戻す
   * ※召喚時効果はtriggerで処理、常時効果はcontinuousEffectsで処理
   */
  C0000028: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, setMyField, myGraveyard } = getPlayerContext(context);

    // 基本技：場の［ドラゴン］モンスター1体のHPを500回復する
    if (context.skillType === 'basic') {
      // 場の【ドラゴン】モンスターを探す
      const dragonIndices = myField
        .map((m, i) => (m && hasCategory(m, '【ドラゴン】')) ? i : -1)
        .filter(i => i >= 0);

      if (dragonIndices.length === 0) {
        addLog('炎竜母フレイマの基本技: 場に【ドラゴン】モンスターがいません', 'info');
        return false;
      }

      // AIの場合は自動選択（HPが最も減っているドラゴンを選ぶ）
      if (!setPendingMonsterTarget) {
        const targetIndex = dragonIndices.reduce((best, idx) => {
          const m = myField[idx];
          if (!m) return best;
          const damage = (m.hp ?? 0) - m.currentHp;
          const bestM = myField[best];
          const bestDamage = bestM ? (bestM.hp ?? 0) - bestM.currentHp : -1;
          return damage > bestDamage ? idx : best;
        }, dragonIndices[0]);

        setMyField(prev => {
          const newField = [...prev];
          const target = newField[targetIndex];
          if (target) {
            const maxHp = target.hp ?? target.currentHp;
            const newHp = Math.min(target.currentHp + 500, maxHp);
            addLog(`炎竜母フレイマの基本技: ${target.name}のHPを500回復！（${target.currentHp}→${newHp}）`, 'heal');
            newField[targetIndex] = { ...target, currentHp: newHp };
          }
          return newField;
        });
        return true;
      }

      // プレイヤーの場合は選択UI
      setPendingMonsterTarget({
        message: 'HPを回復する【ドラゴン】モンスターを選択',
        targetPlayer: 'self',
        validTargets: dragonIndices,
        callback: (selectedIndex: number) => {
          setMyField(prev => {
            const newField = [...prev];
            const target = newField[selectedIndex];
            if (target) {
              const maxHp = target.hp ?? target.currentHp;
              const newHp = Math.min(target.currentHp + 500, maxHp);
              addLog(`炎竜母フレイマの基本技: ${target.name}のHPを500回復！（${target.currentHp}→${newHp}）`, 'heal');
              newField[selectedIndex] = { ...target, currentHp: newHp };
            }
            return newField;
          });
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // 上級技：墓地の［ドラゴン］モンスター1体を攻撃力半減で場に戻す
    if (context.skillType === 'advanced') {
      const success = reviveFromGraveyard(context, (card) => {
        return card.type === 'monster' && hasCategory(card, '【ドラゴン】');
      }, true); // true = attackHalf

      if (!success) {
        addLog('炎竜母フレイマの上級技: 墓地に【ドラゴン】モンスターがいないか、場に空きがありません', 'info');
      }
      return success;
    }

    // 召喚時効果はfireCards.jsのtriggerで処理
    // 常時効果はcontinuousEffects/monsterCards.jsで処理
    return false;
  },

  /**
   * C0000161: 灯魔龍ランプデビル
   * 【召喚時】場にいる全ての炎属性モンスターに3000ダメージを与え、破壊したモンスター×600のダメージを相手プレイヤーに与える
   */
  C0000161: (skillText, context) => {
    const { addLog } = context;
    const {
      setP1Field, setP2Field,
      setP1Graveyard, setP2Graveyard,
    } = context;

    if (skillText.includes('【召喚時】')) {
      let destroyedCount = 0;

      // 両プレイヤーのフィールドの炎属性モンスターにダメージ
      [1, 2].forEach(player => {
        const setField = player === 1 ? setP1Field : setP2Field;
        const setGraveyard = player === 1 ? setP1Graveyard : setP2Graveyard;

        const destroyedMonsters: MonsterInstance[] = [];

        setField((prev: (MonsterInstance | null)[]) => prev.map((m: MonsterInstance | null) => {
          if (m && m.attribute === '炎') {
            const newHp = m.currentHp - 3000;
            if (newHp <= 0) {
              destroyedMonsters.push(m);
              destroyedCount++;
              return null;
            }
            return { ...m, currentHp: newHp };
          }
          return m;
        }));

        if (destroyedMonsters.length > 0) {
          setGraveyard((prev: CardWithId[]) => [...prev, ...destroyedMonsters as unknown as CardWithId[]]);
        }
      });

      addLog(`炎属性モンスターに3000ダメージ！${destroyedCount}体破壊`, 'damage');

      // 破壊したモンスター×600ダメージを相手に
      if (destroyedCount > 0) {
        const damage = destroyedCount * 600;
        conditionalDamage(context, damage, 'opponent');
      }

      return true;
    }
    return false;
  },

  /**
   * C0000163: 岩狸・石ころ丸
   * 基本技：チャージを消費してデッキから【ビースト・狸】モンスター1体を手札に加える
   */
  C0000163: (skillText, context) => {
    if (context.skillType === 'basic') {
      return searchCard(context, (card) => {
        return hasCategory(card, '【ビースト・狸】');
      }) !== null;
    }
    return false;
  },

  /**
   * C0000164: 岩狸・岩太
   * 基本技：自分の【ビースト・狸】モンスター1体のHPを300下げ、攻撃力を300アップ
   */
  C0000164: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, setMyField } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // 自分のフィールドから【ビースト・狸】を探す
      const tanukiTargets = myField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster && hasCategory(monster, '【ビースト・狸】'));

      if (tanukiTargets.length === 0) {
        addLog('岩狸・岩太の基本技: 【ビースト・狸】モンスターがいません', 'info');
        return false;
      }

      const applyEffect = (targetIndex: number) => {
        setMyField(prev => prev.map((m, idx) => {
          if (idx === targetIndex && m) {
            const newHp = Math.max(1, m.currentHp - 300); // HPは最低1
            const newAtk = (m.currentAttack ?? m.attack ?? 0) + 300;
            addLog(`岩狸・岩太の基本技: ${m.name}のHP-300、攻撃力+300！`, 'info');
            return {
              ...m,
              currentHp: newHp,
              currentAttack: newAtk,
              attack: (m.attack ?? 0) + 300,
            };
          }
          return m;
        }));
      };

      // 1体のみの場合は自動選択
      if (tanukiTargets.length === 1) {
        applyEffect(tanukiTargets[0].index);
        return true;
      }

      // 複数いる場合は選択UI
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: 'HP-300、攻撃力+300する【ビースト・狸】を選択',
          targetPlayer: 'self',
          callback: (selectedIndex: number) => {
            applyEffect(selectedIndex);
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      // フォールバック
      applyEffect(tanukiTargets[0].index);
      return true;
    }
    return false;
  },

  /**
   * C0000165: 岩狸・熔岩守
   * 【召喚時】相手モンスター1体の攻撃力をターン終了時まで500ダウン
   */
  C0000165: (skillText, context) => {
    if (skillText.includes('【召喚時】')) {
      return modifyAttack(context, -500, 0, true, false);
    }
    return false;
  },

  /**
   * C0000166: 岩狸・地割れ狸
   * 基本技：自分のSPを1減らすことで相手の場にモンスターがいてもこのカードはダイレクトアタック可能
   */
  C0000166: (skillText, context) => {
    const { addLog, monsterIndex } = context;
    const { myActiveSP, setMyActiveSP, setMyRestedSP, myField, setMyField } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      if (myActiveSP < 1) {
        addLog('岩狸・地割れ狸の基本技: SPが不足しています', 'info');
        return false;
      }

      // SP消費
      setMyActiveSP(prev => prev - 1);
      setMyRestedSP(prev => prev + 1);

      // このモンスターにダイレクトアタック可能フラグを付与
      setMyField(prev => prev.map((m, idx) => {
        if (idx === monsterIndex && m) {
          addLog(`岩狸・地割れ狸の基本技: このターン、ダイレクトアタック可能！`, 'info');
          return {
            ...m,
            canDirectAttack: true, // ダイレクトアタック許可フラグ
          };
        }
        return m;
      }));

      return true;
    }
    return false;
  },

  /**
   * C0000167: 岩狸・剛石権蔵
   * 【召喚時】墓地の［マグマフォージ］モンスター1体を場に戻す（攻撃力半分）
   */
  C0000167: (skillText, context) => {
    if (skillText.includes('【召喚時】')) {
      return reviveFromGraveyard(context, (card) => {
        return hasCategory(card, '【マグマフォージ】');
      }, true);
    }
    return false;
  },

  /**
   * C0000169: 岩狸・火山頭
   * 【召喚時】自分のSPを1減らすことで相手の場にいるモンスター1体に2000ダメージ
   */
  C0000169: (skillText, context) => {
    const { addLog } = context;
    const { myActiveSP, setMyActiveSP, setMyRestedSP } = getPlayerContext(context);

    if (skillText.includes('【召喚時】')) {
      if (myActiveSP >= 1) {
        // SP消費
        setMyActiveSP(prev => prev - 1);
        setMyRestedSP(prev => prev + 1);

        conditionalDamage(context, 2000, 'opponent_monster', 0);
        return true;
      } else {
        addLog('SPが足りません', 'info');
        return false;
      }
    }
    return false;
  },

  /**
   * C0000171: 岩狸・熔岩権蔵・極
   * 【召喚時】自分の場にいる《岩狸》モンスター1体を破壊し、その攻撃力の半分のダメージを相手プレイヤーに与える
   */
  C0000171: (skillText, context) => {
    const { addLog } = context;
    const { myField, setMyField, setMyGraveyard } = getPlayerContext(context);

    if (skillText.includes('【召喚時】')) {
      const tanukiMonsters = myField.filter((m): m is MonsterInstance => m !== null && m.name !== undefined && m.name.includes('岩狸'));

      if (tanukiMonsters.length > 0) {
        const target = tanukiMonsters[0];
        if (!target) {
          addLog('岩狸モンスターがいません', 'info');
          return false;
        }
        const damage = Math.floor((target.attack ?? 0) / 2);

        // モンスターを破壊
        setMyField(prev => prev.map(m => {
          if (m && m.uniqueId === target.uniqueId) {
            return null;
          }
          return m;
        }));
        setMyGraveyard(prev => [...prev, target as unknown as CardWithId]);

        addLog(`${target.name}を破壊`, 'damage');

        // ダメージを与える
        conditionalDamage(context, damage, 'opponent');
        return true;
      } else {
        addLog('岩狸モンスターがいません', 'info');
        return false;
      }
    }
    return false;
  },

  /**
   * C0000172: 岩狸・禁忌の熔岩帝
   * 基本技：自分の《岩狸》モンスター1体に3000ダメージを与え、相手プレイヤーに800ダメージ
   */
  C0000172: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, setMyField, setMyGraveyard } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // 自分の《岩狸》モンスターを探す（自分自身以外）
      const tanukiTargets = myField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster && monster.name && monster.name.includes('岩狸'));

      if (tanukiTargets.length === 0) {
        addLog('岩狸・禁忌の熔岩帝の基本技: 《岩狸》モンスターがいません', 'info');
        return false;
      }

      const applyEffect = (targetIndex: number) => {
        const targetMonster = myField[targetIndex];
        if (!targetMonster) return;

        const newHp = targetMonster.currentHp - 3000;

        if (newHp <= 0) {
          // 破壊して墓地へ
          setMyField(prev => prev.map((m, idx) => idx === targetIndex ? null : m));
          setMyGraveyard(prev => [...prev, targetMonster]);
          addLog(`岩狸・禁忌の熔岩帝の基本技: ${targetMonster.name}に3000ダメージで破壊！`, 'damage');
        } else {
          // ダメージのみ
          setMyField(prev => prev.map((m, idx) => {
            if (idx === targetIndex && m) {
              return { ...m, currentHp: newHp };
            }
            return m;
          }));
          addLog(`岩狸・禁忌の熔岩帝の基本技: ${targetMonster.name}に3000ダメージ！`, 'damage');
        }

        // 相手に800ダメージ
        conditionalDamage(context, 800, 'opponent');
      };

      // 1体のみの場合は自動選択
      if (tanukiTargets.length === 1) {
        applyEffect(tanukiTargets[0].index);
        return true;
      }

      // 複数いる場合は選択UI
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: '3000ダメージを与える《岩狸》モンスターを選択',
          targetPlayer: 'self',
          callback: (selectedIndex: number) => {
            applyEffect(selectedIndex);
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      // フォールバック
      applyEffect(tanukiTargets[0].index);
      return true;
    }
    return false;
  },

  /**
   * C0000369: 炎翼の鳥民・イグニス
   * 【召喚時】自分の手札1枚を墓地に送り、相手プレイヤーに600ダメージを与える
   */
  C0000369: (skillText, context) => {
    const { addLog } = context;
    const { myHand, setMyHand, setMyGraveyard } = getPlayerContext(context);

    if (skillText.includes('【召喚時】')) {
      if (myHand.length > 0) {
        const discardedCard = myHand[0];
        setMyHand(prev => prev.slice(1));
        setMyGraveyard(prev => [...prev, discardedCard]);
        addLog(`手札から「${discardedCard.name}」を墓地に送った`, 'info');

        conditionalDamage(context, 600, 'opponent');
        return true;
      } else {
        addLog('手札がありません', 'info');
        return false;
      }
    }
    return false;
  },

  /**
   * C0000377: 虹羽密林の赤花蔓・カルティノス
   * 【召喚時】自分のデッキの上から2枚を墓地に送り、その中に《虹羽密林》モンスターが含まれていた場合、相手プレイヤーに300ダメージを与える
   */
  C0000377: (skillText, context) => {
    const { addLog } = context;

    if (skillText.includes('【召喚時】')) {
      const milledCards = millDeck(context, 2);

      if (milledCards.length > 0) {
        const hasRainbowMonster = milledCards.some(card =>
          card.name && card.name.includes('虹羽密林')
        );

        if (hasRainbowMonster) {
          conditionalDamage(context, 300, 'opponent');
          addLog('虹羽密林モンスターが含まれていた！', 'info');
        }
        return true;
      }
      return false;
    }
    return false;
  },

  /**
   * C0000025: ブレイズ・ドラゴン
   * 基本技：自身の攻撃力の半分のダメージを相手モンスター1体に与える
   */
  C0000025: (skillText, context) => {
    const { addLog, monsterIndex, setPendingMonsterTarget } = context;
    const { myField, opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      const monster = myField[monsterIndex];
      if (!monster) {
        addLog('モンスターが存在しません', 'damage');
        return false;
      }

      // ダメージ量 = 攻撃力の半分
      const damage = Math.floor((monster.attack ?? 0) / 2);

      // 相手フィールドのモンスターをチェック
      const validTargets = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(t => t.monster !== null);

      if (validTargets.length === 0) {
        addLog('相手フィールドにモンスターがいません', 'info');
        return true; // 対象がいなくても技の発動自体は成功として扱う
      }

      // ダメージを与えてHP0なら破壊する関数
      const applyDamage = (targetIndex: number) => {
        const targetMonster = opponentField[targetIndex];
        if (!targetMonster) return;

        const newHp = targetMonster.currentHp - damage;
        addLog(`${monster.name}の基本技: ${targetMonster.name}に${damage}ダメージ！`, 'damage');

        if (newHp <= 0) {
          addLog(`${targetMonster.name}は破壊された！`, 'damage');
          setOpponentField(prev => prev.map((m, idx) => idx === targetIndex ? null : m));
          setOpponentGraveyard(prev => [...prev, targetMonster]);
        } else {
          setOpponentField(prev => prev.map((m, idx) => {
            if (idx === targetIndex && m) {
              return { ...m, currentHp: newHp };
            }
            return m;
          }));
        }
      };

      // 1体のみの場合は自動選択
      if (validTargets.length === 1) {
        applyDamage(validTargets[0].index);
        return true;
      }

      // 複数の場合はターゲット選択UI
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: `${damage}ダメージを与える相手モンスターを選択`,
          targetPlayer: 'opponent',
          callback: (targetIndex: number) => {
            const targetMonster = opponentField[targetIndex];
            if (!targetMonster) return;

            const newHp = targetMonster.currentHp - damage;
            addLog(`${monster.name}の基本技: ${targetMonster.name}に${damage}ダメージ！`, 'damage');

            if (newHp <= 0) {
              addLog(`${targetMonster.name}は破壊された！`, 'damage');
              setOpponentField(prev => prev.map((m, idx) => idx === targetIndex ? null : m));
              setOpponentGraveyard(prev => [...prev, targetMonster]);
            } else {
              setOpponentField(prev => prev.map((m, idx) => {
                if (idx === targetIndex && m) {
                  return { ...m, currentHp: newHp };
                }
                return m;
              }));
            }
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      // フォールバック: 最初のターゲットを選択
      applyDamage(validTargets[0].index);
      return true;
    }
    return false;
  },

  /**
   * C0000170: 岩狸・大岩王
   * 上級技：チャージを2消費して、デッキから「岩狸」モンスター1体を場に召喚する。
   */
  C0000170: (skillText, context) => {
    const { addLog, currentPlayer, summoningSickness = false } = context;
    const { myDeck, setMyDeck, myField, setMyField } = getPlayerContext(context);

    if (context.skillType === 'advanced') {
      // デッキから岩狸モンスターを探す
      const tanukiIndex = myDeck.findIndex(card =>
        card.type === 'monster' && card.name && card.name.includes('岩狸')
      );

      if (tanukiIndex === -1) {
        addLog('岩狸・大岩王の上級技: デッキに岩狸モンスターがいません', 'info');
        return false;
      }

      // 空きスロットを探す
      const emptySlotIndex = myField.findIndex(slot => slot === null);
      if (emptySlotIndex === -1) {
        addLog('岩狸・大岩王の上級技: フィールドに空きがありません', 'info');
        return false;
      }

      const targetCard = myDeck[tanukiIndex];

      // モンスターインスタンスを作成
      const monsterInstance: MonsterInstance = {
        ...targetCard,
        uniqueId: `${targetCard.id}_${Date.now()}_${Math.random()}`,
        currentHp: targetCard.hp ?? 0,
        currentAttack: targetCard.attack ?? 0,
        canAttack: !summoningSickness, // 召喚酔い設定に依存
        usedSkillThisTurn: false,
        owner: currentPlayer,
        charges: [],
        statusEffects: [],
      };

      // デッキから除去
      const newDeck = [...myDeck];
      newDeck.splice(tanukiIndex, 1);
      setMyDeck(newDeck);

      // フィールドに召喚
      setMyField(prev => {
        const newField = [...prev];
        newField[emptySlotIndex] = monsterInstance;
        return newField;
      });

      addLog(`岩狸・大岩王の上級技: デッキから「${targetCard.name}」を召喚！`, 'info');
      return true;
    }
    return false;
  },

  // ========================================
  // 魔法カード
  // ========================================

  /**
   * C0000173: 熔岩の呼び声
   * デッキからコスト3以下の【マグマフォージ】モンスター1体を場に召喚（コスト不要）
   */
  C0000173: (skillText, context) => {
    const { addLog, currentPlayer, summoningSickness = false } = context;
    const { myDeck, setMyDeck, myField, setMyField } = getPlayerContext(context);

    // デッキからコスト3以下のマグマフォージモンスターを探す
    const targetIndex = myDeck.findIndex(card =>
      card.type === 'monster' &&
      card.cost <= 3 &&
      hasCategory(card, '【マグマフォージ】')
    );

    if (targetIndex === -1) {
      addLog('熔岩の呼び声: デッキにコスト3以下の【マグマフォージ】モンスターがいません', 'info');
      return false;
    }

    // 空きスロットを探す
    const emptySlotIndex = myField.findIndex(slot => slot === null);
    if (emptySlotIndex === -1) {
      addLog('熔岩の呼び声: フィールドに空きがありません', 'info');
      return false;
    }

    const targetCard = myDeck[targetIndex];

    // モンスターインスタンスを作成
    const monsterInstance: MonsterInstance = {
      ...targetCard,
      uniqueId: `${targetCard.id}_${Date.now()}_${Math.random()}`,
      currentHp: targetCard.hp ?? 0,
      currentAttack: targetCard.attack ?? 0,
      canAttack: !summoningSickness, // 召喚酔い設定に依存
      usedSkillThisTurn: false,
      owner: currentPlayer,
      charges: [],
      statusEffects: [],
    };

    // デッキから除去
    const newDeck = [...myDeck];
    newDeck.splice(targetIndex, 1);
    setMyDeck(newDeck);

    // フィールドに召喚
    setMyField(prev => {
      const newField = [...prev];
      newField[emptySlotIndex] = monsterInstance;
      return newField;
    });

    addLog(`熔岩の呼び声: デッキから「${targetCard.name}」を召喚！`, 'info');
    return true;
  },

  /**
   * C0000174: 岩狸の咆哮
   * 場にいる【ビースト・狸】モンスター1体の攻撃力をターン終了時まで800アップし、相手モンスター1体の攻撃力を600ダウン
   */
  C0000174: (skillText, context) => {
    const { addLog } = context;
    const { myField, setMyField, opponentField, setOpponentField } = getPlayerContext(context);

    // 【ビースト・狸】モンスターを探す
    const tanukiIndex = myField.findIndex(m =>
      m && hasCategory(m, '【ビースト・狸】')
    );

    if (tanukiIndex === -1) {
      addLog('岩狸の咆哮: 【ビースト・狸】モンスターがいません', 'info');
      return false;
    }

    // 攻撃力800アップ
    setMyField(prev => {
      const newField = [...prev];
      const tanukiMonster = newField[tanukiIndex];
      if (tanukiMonster) {
        const newAttack = (tanukiMonster.currentAttack ?? tanukiMonster.attack ?? 0) + 800;
        newField[tanukiIndex] = {
          ...tanukiMonster,
          attack: (tanukiMonster.attack ?? 0) + 800,
          currentAttack: newAttack,
        };
        addLog(`岩狸の咆哮: ${tanukiMonster.name}の攻撃力+800！`, 'heal');
      }
      return newField;
    });

    // 相手モンスター1体の攻撃力を600ダウン
    const opponentMonsterIndex = opponentField.findIndex(m => m !== null);
    if (opponentMonsterIndex !== -1) {
      setOpponentField(prev => {
        const newField = [...prev];
        const oppMonster = newField[opponentMonsterIndex];
        if (oppMonster) {
          const newAttack = Math.max(0, (oppMonster.currentAttack ?? oppMonster.attack ?? 0) - 600);
          newField[opponentMonsterIndex] = {
            ...oppMonster,
            attack: Math.max(0, (oppMonster.attack ?? 0) - 600),
            currentAttack: newAttack,
          };
          addLog(`岩狸の咆哮: ${oppMonster.name}の攻撃力-600！`, 'damage');
        }
        return newField;
      });
    }

    return true;
  },

  /**
   * C0000179: 熔岩再生
   * 墓地のコスト4以下の《岩狸》モンスター2体を場に戻す（攻撃力は半分、HPは半分）
   */
  C0000179: (skillText, context) => {
    const { addLog, currentPlayer, summoningSickness = false } = context;
    const { myGraveyard, setMyGraveyard, myField, setMyField } = getPlayerContext(context);

    // 墓地からコスト4以下の岩狸モンスターを探す
    const tanukiCards = myGraveyard.filter(card =>
      card.type === 'monster' &&
      card.cost <= 4 &&
      card.name && card.name.includes('岩狸')
    );

    if (tanukiCards.length === 0) {
      addLog('熔岩再生: 墓地にコスト4以下の岩狸モンスターがいません', 'info');
      return false;
    }

    // 空きスロットを確認
    const emptySlots = myField.reduce((acc: number[], slot, idx) => {
      if (slot === null) acc.push(idx);
      return acc;
    }, [] as number[]);

    if (emptySlots.length === 0) {
      addLog('熔岩再生: フィールドに空きがありません', 'info');
      return false;
    }

    // 最大2体まで蘇生
    const reviveCount = Math.min(tanukiCards.length, emptySlots.length, 2);
    const toRevive = tanukiCards.slice(0, reviveCount);
    const revivedIds = toRevive.map(c => c.uniqueId || c.id);

    // 墓地から除去
    setMyGraveyard(prev => prev.filter(c => !revivedIds.includes(c.uniqueId || c.id)));

    // フィールドに蘇生
    setMyField(prev => {
      const newField = [...prev];
      toRevive.forEach((card, i) => {
        const slotIndex = emptySlots[i];
        newField[slotIndex] = {
          ...card,
          uniqueId: `${card.id}_revive_${Date.now()}_${Math.random()}_${i}`,
          attack: Math.floor(((card as CardData).attack ?? 0) / 2),
          currentAttack: Math.floor(((card as CardData).attack ?? 0) / 2),
          hp: Math.floor(((card as CardData).hp ?? 0) / 2),
          currentHp: Math.floor(((card as CardData).hp ?? 0) / 2),
          canAttack: !summoningSickness, // 召喚酔い設定に依存
          usedSkillThisTurn: false,
          owner: currentPlayer,
          charges: [],
          statusEffects: [],
        };
        addLog(`熔岩再生: 「${card.name}」を蘇生！（攻撃力${Math.floor(((card as CardData).attack ?? 0) / 2)}、HP${Math.floor(((card as CardData).hp ?? 0) / 2)}）`, 'info');
      });
      return newField;
    });

    return true;
  },

  /**
   * C0000160: 業火の爆炎
   * 自分の場にいる炎属性モンスター1体を破壊し、その攻撃力の半分を相手プレイヤーに与える
   */
  C0000160: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, setMyField, setMyGraveyard } = getPlayerContext(context);

    // 自分の炎属性モンスターを探す
    const fireMonsters = myField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster && monster.attribute === '炎');

    if (fireMonsters.length === 0) {
      addLog('業火の爆炎: 炎属性モンスターがいません', 'info');
      return false;
    }

    const executeEffect = (targetIndex: number) => {
      const targetMonster = myField[targetIndex];
      if (!targetMonster) return;

      const damage = Math.floor((targetMonster?.attack ?? 0) / 2);

      // モンスターを破壊
      setMyField(prev => prev.map((m, idx) => idx === targetIndex ? null : m));
      setMyGraveyard(prev => [...prev, targetMonster]);
      addLog(`業火の爆炎: ${targetMonster.name}を破壊！`, 'damage');

      // 相手にダメージ
      conditionalDamage(context, damage, 'opponent');
    };

    // 1体のみの場合は自動選択
    if (fireMonsters.length === 1) {
      executeEffect(fireMonsters[0].index);
      return true;
    }

    // 複数いる場合は選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '破壊する炎属性モンスターを選択',
        targetPlayer: 'self',
        callback: (selectedIndex: number) => {
          executeEffect(selectedIndex);
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // フォールバック
    executeEffect(fireMonsters[0].index);
    return true;
  },

  /**
   * C0000175: 地層の炎流
   * 場にいる炎属性モンスターの数×300ダメージを相手プレイヤーに与える
   */
  C0000175: (skillText, context) => {
    const { addLog } = context;
    const { myField } = getPlayerContext(context);

    // 炎属性モンスターの数をカウント
    const fireCount = myField.filter(m => m && m.attribute === '炎').length;

    if (fireCount === 0) {
      addLog('地層の炎流: 炎属性モンスターがいません', 'info');
      return false;
    }

    const damage = fireCount * 300;
    addLog(`地層の炎流: 炎属性${fireCount}体×300 = ${damage}ダメージ！`, 'damage');
    conditionalDamage(context, damage, 'opponent');

    return true;
  },

  /**
   * C0000176: 熔岩の罠
   * このターンに召喚されたモンスターの攻撃力を半分にし、次のターンまで行動不能にする
   * 【刹那詠唱】
   */
  C0000176: (skillText, context) => {
    const { addLog } = context;
    const { opponentField, setOpponentField } = getPlayerContext(context);

    // このターンに召喚されたモンスター（canAttack = false のモンスター）を対象
    const summonedThisTurn = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster && monster.canAttack === false);

    if (summonedThisTurn.length === 0) {
      addLog('熔岩の罠: このターンに召喚されたモンスターがいません', 'info');
      return false;
    }

    setOpponentField(prev => prev.map((m, idx) => {
      if (m && m.canAttack === false) {
        const newAtk = Math.floor(((m.currentAttack ?? m.attack ?? 0)) / 2);
        addLog(`熔岩の罠: ${m.name}の攻撃力を${newAtk}に半減、行動不能！`, 'damage');
        return {
          ...m,
          currentAttack: newAtk,
          attack: Math.floor((m.attack ?? 0) / 2),
          stunned: true, // 行動不能
          stunnedUntilNextTurn: true, // 次のターンまで
        };
      }
      return m;
    }));

    return true;
  },

  /**
   * C0000177: 岩石の守護
   * このターン、自分の炎属性モンスター1体はダメージを受けない
   */
  C0000177: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, setMyField } = getPlayerContext(context);

    // 炎属性モンスターを探す
    const fireMonsters = myField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster && monster.attribute === '炎');

    if (fireMonsters.length === 0) {
      addLog('岩石の守護: 炎属性モンスターがいません', 'info');
      return false;
    }

    const applyProtection = (targetIndex: number) => {
      setMyField(prev => prev.map((m, idx) => {
        if (idx === targetIndex && m) {
          addLog(`岩石の守護: ${m.name}はこのターン、ダメージを受けない！`, 'heal');
          return {
            ...m,
            damageImmunity: true, // ダメージ無効
            damageImmunityUntilEndOfTurn: true,
          };
        }
        return m;
      }));
    };

    // 1体のみの場合は自動選択
    if (fireMonsters.length === 1) {
      applyProtection(fireMonsters[0].index);
      return true;
    }

    // 複数いる場合は選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '守護を与える炎属性モンスターを選択',
        targetPlayer: 'self',
        callback: (selectedIndex: number) => {
          applyProtection(selectedIndex);
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // フォールバック
    applyProtection(fireMonsters[0].index);
    return true;
  },

  /**
   * C0000178: 狸火の誘い
   * 相手モンスター1体の攻撃先を自分の別のモンスターに変更し、その相手モンスターの攻撃力を500ダウン
   * 【刹那詠唱】
   * ※攻撃宣言時に使用する想定のため、攻撃先変更は戦闘処理で対応が必要
   *   ここでは攻撃力ダウンのみ実装
   */
  C0000178: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField } = getPlayerContext(context);

    // 相手モンスターを探す
    const opponentMonsters = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster !== null);

    if (opponentMonsters.length === 0) {
      addLog('狸火の誘い: 相手フィールドにモンスターがいません', 'info');
      return false;
    }

    const applyEffect = (targetIndex: number) => {
      setOpponentField(prev => prev.map((m, idx) => {
        if (idx === targetIndex && m) {
          const newAtk = Math.max(0, (m.currentAttack ?? m.attack ?? 0) - 500);
          addLog(`狸火の誘い: ${m.name}の攻撃力-500！`, 'damage');
          return {
            ...m,
            currentAttack: newAtk,
            attack: Math.max(0, (m.attack ?? 0) - 500),
            attackRedirected: true, // 攻撃先変更フラグ（戦闘処理で使用）
          };
        }
        return m;
      }));
    };

    // 1体のみの場合は自動選択
    if (opponentMonsters.length === 1) {
      applyEffect(opponentMonsters[0].index);
      return true;
    }

    // 複数いる場合は選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '攻撃力を下げる相手モンスターを選択',
        targetPlayer: 'opponent',
        callback: (selectedIndex: number) => {
          applyEffect(selectedIndex);
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // フォールバック
    applyEffect(opponentMonsters[0].index);
    return true;
  },

  /**
   * C0000180: 地割れの怒り
   * 場にいる【マグマフォージ】モンスター1体を破壊し、その攻撃力の半分＋1000ダメージを相手に与える
   */
  C0000180: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, setMyField, setMyGraveyard } = getPlayerContext(context);

    // 【マグマフォージ】モンスターを探す
    const magmaMonsters = myField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster && hasCategory(monster, '【マグマフォージ】'));

    if (magmaMonsters.length === 0) {
      addLog('地割れの怒り: 【マグマフォージ】モンスターがいません', 'info');
      return false;
    }

    const executeEffect = (targetIndex: number) => {
      const targetMonster = myField[targetIndex];
      if (!targetMonster) return;

      const damage = Math.floor((targetMonster?.attack ?? 0) / 2) + 1000;

      // モンスターを破壊
      setMyField(prev => prev.map((m, idx) => idx === targetIndex ? null : m));
      setMyGraveyard(prev => [...prev, targetMonster]);
      addLog(`地割れの怒り: ${targetMonster.name}を破壊！`, 'damage');

      // 相手にダメージ
      addLog(`地割れの怒り: 攻撃力${(targetMonster?.attack ?? 0)}の半分+1000 = ${damage}ダメージ！`, 'damage');
      conditionalDamage(context, damage, 'opponent');
    };

    // 1体のみの場合は自動選択
    if (magmaMonsters.length === 1) {
      executeEffect(magmaMonsters[0].index);
      return true;
    }

    // 複数いる場合は選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '破壊する【マグマフォージ】モンスターを選択',
        targetPlayer: 'self',
        callback: (selectedIndex: number) => {
          executeEffect(selectedIndex);
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // フォールバック
    executeEffect(magmaMonsters[0].index);
    return true;
  },

  /**
   * C0000181: 岩狸の覚醒
   * 場にいる炎属性モンスター1体の攻撃力をターン終了時まで倍にする
   */
  C0000181: (skillText, context) => {
    const { addLog } = context;
    const { myField, setMyField } = getPlayerContext(context);

    // 炎属性モンスターを探す
    const fireMonsterIndex = myField.findIndex(m => m && m.attribute === '炎');

    if (fireMonsterIndex === -1) {
      addLog('岩狸の覚醒: 炎属性モンスターがいません', 'info');
      return false;
    }

    // 攻撃力を倍にする
    setMyField(prev => {
      const newField = [...prev];
      const monster = newField[fireMonsterIndex];
      if (monster) {
        const currentAtk = monster.currentAttack ?? monster.attack ?? 0;
        const doubledAtk = currentAtk * 2;
        newField[fireMonsterIndex] = {
          ...monster,
          attack: doubledAtk,
          currentAttack: doubledAtk,
        };
        addLog(`岩狸の覚醒: ${monster.name}の攻撃力が${currentAtk}→${doubledAtk}に倍化！`, 'heal');
      }
      return newField;
    });

    return true;
  },

  /**
   * C0000184: 狸の業火
   * 自分の場にいる《岩狸》モンスター1体を破壊し、その攻撃力分のダメージを相手に与える
   */
  C0000184: (skillText, context) => {
    const { addLog } = context;
    const { myField, setMyField, setMyGraveyard, setOpponentLife } = getPlayerContext(context);

    // 岩狸モンスターを探す
    const tanukiIndex = myField.findIndex(m => m && m.name && m.name.includes('岩狸'));

    if (tanukiIndex === -1) {
      addLog('狸の業火: 岩狸モンスターがいません', 'info');
      return false;
    }

    const targetMonster = myField[tanukiIndex];
    if (!targetMonster) {
      addLog('狸の業火: 岩狸モンスターがいません', 'info');
      return false;
    }
    const damage = targetMonster.currentAttack ?? targetMonster.attack ?? 0;

    // モンスターを破壊
    setMyField(prev => {
      const newField = [...prev];
      newField[tanukiIndex] = null;
      return newField;
    });

    // 墓地に送る
    setMyGraveyard(prev => [...prev, targetMonster as unknown as CardWithId]);

    // 相手にダメージ
    setOpponentLife(prev => prev - damage);

    addLog(`狸の業火: ${targetMonster.name}を破壊！`, 'damage');
    addLog(`狸の業火: 相手プレイヤーに${damage}ダメージ！`, 'damage');

    return true;
  },

  /**
   * C0000182: 熔岩の奔流
   * 場にいる炎属性モンスターの数×300ダメージを自分と相手プレイヤーに与え、
   * レスト状態のSPトークン1個をアクティブにする。
   */
  C0000182: (skillText, context) => {
    const { addLog } = context;
    const {
      myField, opponentField,
      setMyLife, setOpponentLife,
      myRestedSP, setMyActiveSP, setMyRestedSP,
    } = getPlayerContext(context);

    // 場にいる炎属性モンスターをカウント（両方のフィールド）
    const myFireCount = myField.filter(m => m && m.attribute === '炎').length;
    const oppFireCount = opponentField.filter(m => m && m.attribute === '炎').length;
    const totalFireCount = myFireCount + oppFireCount;

    if (totalFireCount === 0) {
      addLog('熔岩の奔流: 場に炎属性モンスターがいません', 'info');
      return false;
    }

    const damage = totalFireCount * 300;

    // 自分と相手にダメージ
    setMyLife(prev => Math.max(0, prev - damage));
    setOpponentLife(prev => Math.max(0, prev - damage));
    addLog(`熔岩の奔流: 炎属性モンスター${totalFireCount}体！自分と相手に${damage}ダメージ！`, 'damage');

    // レスト状態のSP1個をアクティブに
    if (myRestedSP > 0) {
      setMyActiveSP(prev => prev + 1);
      setMyRestedSP(prev => prev - 1);
      addLog('熔岩の奔流: SPを1回復！', 'heal');
    }

    return true;
  },

  /**
   * C0000023: レッドバーストドラゴン
   * 【覚醒】キーワード持ち
   * 基本技：自分の手札を1枚捨てて、このカードの攻撃力をターン終了時まで1000アップ。
   *         ※基本技発動で覚醒状態になる（覚醒時効果: 攻撃力+1000）
   */
  C0000023: (skillText, context) => {
    const { addLog, monsterIndex, setPendingHandSelection } = context;
    const { myField, myHand, setMyField, setMyHand, setMyGraveyard } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      const monster = myField[monsterIndex];
      if (!monster) {
        addLog('モンスターが存在しません', 'info');
        return false;
      }

      // 手札がない場合は発動不可
      if (myHand.length === 0) {
        addLog('手札がありません', 'info');
        return false;
      }

      // 手札が1枚の場合は自動選択
      if (myHand.length === 1) {
        const discardedCard = myHand[0];
        setMyHand([]);
        setMyGraveyard(prev => [...prev, discardedCard]);
        addLog(`${monster.name}の基本技: 手札から${discardedCard.name}を捨てた`, 'info');

        // 覚醒状態にする（ATK+1000）
        // ※基本技のATK+1000効果は覚醒状態のATK+1000として実装
        applyAwakening(context, monsterIndex, 1000, 'レッドバーストドラゴン');
        return true;
      }

      // 複数の場合は手札選択UI
      if (setPendingHandSelection) {
        setPendingHandSelection({
          message: '捨てるカードを選択してください',
          callback: (selectedCards: CardWithId[]) => {
            if (selectedCards && selectedCards.length > 0) {
              const selectedCard = selectedCards[0];
              setMyHand(prev => prev.filter(c => c.uniqueId !== selectedCard.uniqueId));
              setMyGraveyard(prev => [...prev, selectedCard]);
              addLog(`${monster.name}の基本技: 手札から${selectedCard.name}を捨てた`, 'info');

              // 覚醒状態にする（ATK+1000）
              applyAwakening(context, monsterIndex, 1000, 'レッドバーストドラゴン');
            }
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      return false;
    }

    return false;
  },

  /**
   * C0000033: 紅蓮の覚醒
   * 場にいる［ドラゴン］モンスター1体を、このターン中「覚醒」状態にし、攻撃力を700アップ。
   */
  C0000033: (skillText, context) => {
    const { addLog } = context;
    const { myField } = getPlayerContext(context);

    // ドラゴンモンスターを探す
    const dragonCondition = (monster: MonsterInstance | null): boolean => monster !== null && hasCategory(monster, 'ドラゴン');

    // 条件を満たすモンスターがいるかチェック
    const validTargets = myField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster && dragonCondition(monster));

    if (validTargets.length === 0) {
      addLog('紅蓮の覚醒: 場にドラゴンモンスターがいません', 'info');
      return false;
    }

    // 覚醒を付与（ATK+700）
    return selectAndApplyAwakening(context, dragonCondition, 700, '紅蓮の覚醒');
  },

  // ========================================
  // 魔法カード (ID 30番台)
  // ========================================

  /**
   * C0000031: 炎の咆哮
   * 場にいる［ドラゴン］と名の付いたモンスター1体の攻撃力をターン終了時まで500アップし、相手プレイヤーに300ダメージを与える。
   */
  C0000031: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, setMyField } = getPlayerContext(context);

    // ドラゴンモンスターを探す
    const dragonIndices = myField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster && hasCategory(monster, '【ドラゴン】'));

    if (dragonIndices.length === 0) {
      addLog('炎の咆哮: 場にドラゴンモンスターがいません', 'info');
      return false;
    }

    // ドラゴンに攻撃力+500を付与する関数
    const applyBuff = (targetIndex: number) => {
      setMyField(prev => {
        const newField = [...prev];
        const monster = newField[targetIndex];
        if (monster) {
          const newAttack = (monster.currentAttack ?? monster.attack ?? 0) + 500;
          newField[targetIndex] = {
            ...monster,
            attack: (monster.attack ?? 0) + 500,
            currentAttack: newAttack,
          };
          addLog(`炎の咆哮: ${monster.name}の攻撃力+500！`, 'heal');
        }
        return newField;
      });
    };

    // 1体のみの場合は自動選択
    if (dragonIndices.length === 1) {
      applyBuff(dragonIndices[0].index);
    } else if (setPendingMonsterTarget) {
      // 複数の場合はターゲット選択UI
      setPendingMonsterTarget({
        message: '攻撃力+500するドラゴンを選択',
        validTargets: dragonIndices.map(d => d.index),
        targetPlayer: 'self',
        callback: (targetIndex: number) => {
          applyBuff(targetIndex);
        },
        selectionOwner: context.currentPlayer,
      });
    } else {
      // フォールバック: 最初を選択
      applyBuff(dragonIndices[0].index);
    }

    // 相手に300ダメージ
    conditionalDamage(context, 300, 'opponent');

    return true;
  },

  /**
   * C0000032: ドラゴンの炎息
   * 【刹那詠唱】相手モンスター1体に800ダメージを与えた後、相手モンスター全体に300ダメージを与える。
   */
  C0000032: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    // 相手フィールドのモンスターをチェック
    const validTargets = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(t => t.monster !== null);

    if (validTargets.length === 0) {
      addLog('ドラゴンの炎息: 相手フィールドにモンスターがいません', 'info');
      return true; // 発動自体は成功
    }

    // 800ダメージを与える関数
    const apply800Damage = (targetIndex: number) => {
      let destroyedMonster: MonsterInstance | null = null;

      setOpponentField(prev => {
        const newField = [...prev];
        const m = newField[targetIndex];
        if (m) {
          const newHp = m.currentHp - 800;
          addLog(`ドラゴンの炎息: ${m.name}に800ダメージ！`, 'damage');
          if (newHp <= 0) {
            addLog(`${m.name}は破壊された！`, 'damage');
            destroyedMonster = m;
            newField[targetIndex] = null;
          } else {
            newField[targetIndex] = { ...m, currentHp: newHp };
          }
        }
        return newField;
      });

      if (destroyedMonster) {
        setOpponentGraveyard(prev => [...prev, destroyedMonster as unknown as CardWithId]);
      }

      // 全体に300ダメージ
      applyAoeDamage();
    };

    // 全体300ダメージ
    const applyAoeDamage = () => {
      const destroyedMonsters: MonsterInstance[] = [];

      setOpponentField(prev => {
        return prev.map(m => {
          if (m) {
            const newHp = m.currentHp - 300;
            if (newHp <= 0) {
              addLog(`${m.name}が炎息の余波で破壊された！`, 'damage');
              destroyedMonsters.push(m);
              return null;
            }
            return { ...m, currentHp: newHp };
          }
          return m;
        });
      });

      if (destroyedMonsters.length > 0) {
        setOpponentGraveyard(prev => [...prev, ...destroyedMonsters]);
        addLog(`ドラゴンの炎息: 全体に300ダメージ！${destroyedMonsters.length}体破壊`, 'damage');
      } else if (validTargets.length > 0) {
        addLog('ドラゴンの炎息: 全体に300ダメージ！', 'damage');
      }
    };

    // 1体のみの場合は自動選択
    if (validTargets.length === 1) {
      apply800Damage(validTargets[0].index);
    } else if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '800ダメージを与える相手モンスターを選択',
        targetPlayer: 'opponent',
        callback: (targetIndex: number) => {
          apply800Damage(targetIndex);
        },
        selectionOwner: context.currentPlayer,
      });
    } else {
      apply800Damage(validTargets[0].index);
    }

    return true;
  },

  /**
   * C0000034: 炎の嵐
   * 場にいる全ての［ドラゴン］モンスターの数×200ダメージを相手プレイヤーに与える。
   */
  C0000034: (skillText, context) => {
    const { addLog, p1Field, p2Field } = context;

    // 両プレイヤーのフィールドのドラゴンを数える
    const allFields = [...(p1Field || []), ...(p2Field || [])];
    const dragonCount = allFields.filter(m =>
      m && hasCategory(m, '【ドラゴン】')
    ).length;

    if (dragonCount === 0) {
      addLog('炎の嵐: 場にドラゴンがいないためダメージなし', 'info');
      return true;
    }

    const damage = dragonCount * 200;
    addLog(`炎の嵐: ドラゴン${dragonCount}体で${damage}ダメージ！`, 'damage');
    conditionalDamage(context, damage, 'opponent');

    return true;
  },

  /**
   * C0000035: ドラゴンの血脈
   * デッキから《レッドバーストドラゴン》または《炎竜母フレイマ》を場に直接召喚。
   */
  C0000035: (skillText, context) => {
    const { addLog, currentPlayer, summoningSickness = false } = context;
    const { myDeck, setMyDeck, myField, setMyField } = getPlayerContext(context);

    // デッキから対象カードを探す
    const targetNames = ['レッドバーストドラゴン', '炎竜母フレイマ'];
    const targetIndex = myDeck.findIndex(card =>
      card.type === 'monster' && targetNames.includes(card.name)
    );

    if (targetIndex === -1) {
      addLog('ドラゴンの血脈: デッキに対象カードがありません', 'info');
      return false;
    }

    // 空きスロットを確認
    const emptySlotIndex = myField.findIndex(slot => slot === null);
    if (emptySlotIndex === -1) {
      addLog('ドラゴンの血脈: フィールドに空きがありません', 'info');
      return false;
    }

    const targetCard = myDeck[targetIndex];

    // モンスターインスタンスを作成
    const monsterInstance: MonsterInstance = {
      ...targetCard,
      uniqueId: `${targetCard.id}_${Date.now()}_${Math.random()}`,
      currentHp: targetCard.hp ?? 0,
      currentAttack: targetCard.attack ?? 0,
      canAttack: !summoningSickness, // 召喚酔い設定に依存
      usedSkillThisTurn: false,
      owner: currentPlayer,
      charges: [],
      statusEffects: [],
    };

    // デッキから除去
    const newDeck = [...myDeck];
    newDeck.splice(targetIndex, 1);
    setMyDeck(newDeck);

    // フィールドに召喚
    setMyField(prev => {
      const newField = [...prev];
      newField[emptySlotIndex] = monsterInstance;
      return newField;
    });

    addLog(`ドラゴンの血脈: デッキから「${targetCard.name}」を召喚！`, 'info');
    return true;
  },

  /**
   * C0000036: 燃え尽きる業火
   * 自分の場にいる《ドラゴン》と名のついたモンスター1体を破壊し、その攻撃力分のダメージを相手プレイヤーに与える。
   */
  C0000036: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, setMyField, setMyGraveyard, setOpponentLife } = getPlayerContext(context);

    // 自分フィールドのドラゴンを探す
    const dragonIndices = myField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster && hasCategory(monster, '【ドラゴン】'));

    if (dragonIndices.length === 0) {
      addLog('燃え尽きる業火: 場にドラゴンモンスターがいません', 'info');
      return false;
    }

    // ドラゴンを破壊してダメージを与える関数
    const destroyAndDamage = (targetIndex: number) => {
      const targetMonster = myField[targetIndex];
      if (!targetMonster) return;

      const damage = targetMonster.currentAttack || (targetMonster?.attack ?? 0);

      // モンスターを破壊
      setMyField(prev => {
        const newField = [...prev];
        newField[targetIndex] = null;
        return newField;
      });

      // 墓地に送る
      setMyGraveyard(prev => [...prev, targetMonster]);

      // 相手にダメージ
      setOpponentLife(prev => prev - damage);

      addLog(`燃え尽きる業火: ${targetMonster.name}を破壊！`, 'damage');
      addLog(`燃え尽きる業火: 相手プレイヤーに${damage}ダメージ！`, 'damage');
    };

    // 1体のみの場合は自動選択
    if (dragonIndices.length === 1) {
      destroyAndDamage(dragonIndices[0].index);
    } else if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '破壊するドラゴンを選択',
        validTargets: dragonIndices.map(d => d.index),
        targetPlayer: 'self',
        callback: (targetIndex: number) => {
          destroyAndDamage(targetIndex);
        },
        selectionOwner: context.currentPlayer,
      });
    } else {
      destroyAndDamage(dragonIndices[0].index);
    }

    return true;
  },

  /**
   * C0000312: フレア・ワイバーン
   * 基本技: 相手モンスター1体に600ダメージ与える。
   */
  C0000312: (skillText, context) => {
    const { addLog, monsterIndex, setPendingMonsterTarget } = context;
    const { myField, opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      const monster = myField[monsterIndex];
      if (!monster) {
        addLog('モンスターが存在しません', 'info');
        return false;
      }

      const damage = 600;

      // 相手フィールドのモンスターをチェック
      const validTargets = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(t => t.monster !== null);

      if (validTargets.length === 0) {
        addLog('相手フィールドにモンスターがいません', 'info');
        return true;
      }

      const applyDamage = (targetIndex: number) => {
        setOpponentField(prev => {
          const newField = [...prev];
          const target = newField[targetIndex];
          if (target) {
            const newHp = target.currentHp - damage;
            addLog(`フレア・ワイバーンの基本技: ${target.name}に${damage}ダメージ！`, 'damage');
            if (newHp <= 0) {
              addLog(`${target.name}は破壊された！`, 'damage');
              setOpponentGraveyard(prev => [...prev, target]);
              newField[targetIndex] = null;
            } else {
              newField[targetIndex] = { ...target, currentHp: newHp };
            }
          }
          return newField;
        });
      };

      // 1体のみの場合は自動選択
      if (validTargets.length === 1) {
        applyDamage(validTargets[0].index);
        return true;
      }

      // 複数の場合はターゲット選択UI
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: `600ダメージを与える相手モンスターを選択`,
          targetPlayer: 'opponent',
          callback: (targetIndex: number) => {
            applyDamage(targetIndex);
          },
          selectionOwner: context.currentPlayer,
        });
      }
      return true;
    }
    return false;
  },

  // ========================================
  // ヴォランティス関連カード
  // ========================================

  /**
   * C0000359: ヴォランティス・インフェルノ
   * 【召喚時】自分のフィールドのモンスター1体を破壊し、相手プレイヤーに1000ダメージを与える。
   * 【壮麗】: 手札から《ヴォランティス・インフェルノ》を1枚捨て、このターン中、このカードが与える戦闘ダメージを1.5倍にする。
   * 基本技: 相手モンスター1体に1200ダメージを与える（この効果で破壊された場合、相手プレイヤーに追加で500ダメージ）。
   * 上級技: 自分のSPトークンを2個レスト状態にし、相手フィールド全体に1500ダメージを与える。
   * ※召喚時効果はトリガーで実装済み
   */
  C0000359: (skillText, context) => {
    const { addLog, monsterIndex, setPendingMonsterTarget } = context;
    const {
      myField, setMyField,
      myActiveSP, setMyActiveSP, setMyRestedSP,
      opponentField, setOpponentField, setOpponentGraveyard, setOpponentLife,
    } = getPlayerContext(context);

    // 基本技: 相手モンスター1体に1200ダメージ（破壊時追加500ダメージ）
    if (context.skillType === 'basic') {
      const validTargets = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(t => t.monster !== null);

      if (validTargets.length === 0) {
        addLog('ヴォランティス・インフェルノの基本技: 相手フィールドにモンスターがいません', 'info');
        return true;
      }

      const applyDamage = (targetIndex: number) => {
        const target = opponentField[targetIndex];
        if (!target) return;

        const damage = 1200;
        const newHp = (target.currentHp ?? target.hp ?? 0) - damage;

        addLog(`ヴォランティス・インフェルノの基本技: ${target.name}に1200ダメージ！`, 'damage');

        if (newHp <= 0) {
          // 破壊された場合
          setOpponentField(prev => {
            const newField = [...prev];
            newField[targetIndex] = null;
            return newField;
          });
          setOpponentGraveyard(prev => [...prev, target]);
          addLog(`${target.name}は破壊された！`, 'damage');

          // 追加500ダメージ
          setOpponentLife(prev => prev - 500);
          addLog('ヴォランティス・インフェルノの基本技: 破壊ボーナス！相手プレイヤーに500ダメージ！', 'damage');
        } else {
          setOpponentField(prev => {
            const newField = [...prev];
            newField[targetIndex] = { ...target, currentHp: newHp };
            return newField;
          });
        }
      };

      // 1体のみの場合は自動選択
      if (validTargets.length === 1) {
        applyDamage(validTargets[0].index);
        return true;
      }

      // 複数の場合はターゲット選択UI
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: '1200ダメージを与える相手モンスターを選択',
          targetPlayer: 'opponent',
          callback: (targetIndex: number) => {
            applyDamage(targetIndex);
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      // フォールバック
      applyDamage(validTargets[0].index);
      return true;
    }

    // 上級技: SP2個レスト、相手フィールド全体に1500ダメージ
    if (context.skillType === 'advanced') {
      // SP確認
      if (myActiveSP < 2) {
        addLog('ヴォランティス・インフェルノの上級技: アクティブSPが2個以上必要です', 'info');
        return false;
      }

      // SP2個をレスト状態に
      setMyActiveSP(prev => prev - 2);
      setMyRestedSP(prev => prev + 2);
      addLog('ヴォランティス・インフェルノの上級技: SPを2個消費！', 'info');

      // 相手フィールド全体に1500ダメージ
      const destroyedMonsters: MonsterInstance[] = [];
      setOpponentField(prev => prev.map(monster => {
        if (monster) {
          const newHp = (monster.currentHp ?? monster.hp ?? 0) - 1500;
          addLog(`${monster.name}に1500ダメージ！`, 'damage');
          if (newHp <= 0) {
            destroyedMonsters.push(monster);
            return null;
          }
          return { ...monster, currentHp: newHp };
        }
        return monster;
      }));

      // 破壊されたモンスターを墓地に送る
      if (destroyedMonsters.length > 0) {
        setOpponentGraveyard(prev => [...prev, ...destroyedMonsters as unknown as CardWithId[]]);
        destroyedMonsters.forEach(m => {
          addLog(`${m.name}は破壊された！`, 'damage');
        });
      }

      return true;
    }

    return false;
  },

  /**
   * C0000361: 虹羽密林のフェザートラップ
   * 基本技: このカードを破壊し、相手フィールドのモンスター1体に1000ダメージを与える
   */
  C0000361: (skillText, context) => {
    const { addLog, monsterIndex, setPendingMonsterTarget } = context;
    const { myField, setMyField, setMyGraveyard, opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      const monster = myField[monsterIndex];
      if (!monster) {
        addLog('フェザートラップの基本技: 対象が見つかりません', 'info');
        return false;
      }

      // 相手フィールドのモンスターをチェック
      const validTargets = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(t => t.monster !== null);

      if (validTargets.length === 0) {
        addLog('フェザートラップの基本技: 相手フィールドにモンスターがいません', 'info');
        return false;
      }

      // 自身を破壊して墓地に送る関数
      const destroySelf = () => {
        setMyField(prev => {
          const newField = [...prev];
          newField[monsterIndex] = null;
          return newField;
        });
        setMyGraveyard(prev => [...prev, monster]);
        addLog('フェザートラップは自身を破壊した！', 'info');
      };

      // ダメージを与える関数
      const applyDamage = (targetIndex: number) => {
        destroySelf();

        const targetMonster = opponentField[targetIndex];
        if (!targetMonster) return;

        const newHp = (targetMonster.currentHp ?? targetMonster.hp ?? 0) - 1000;
        addLog(`フェザートラップの基本技: ${targetMonster.name}に1000ダメージ！`, 'damage');

        if (newHp <= 0) {
          addLog(`${targetMonster.name}は破壊された！`, 'damage');
          setOpponentField(prev => prev.map((m, idx) => idx === targetIndex ? null : m));
          setOpponentGraveyard(prev => [...prev, targetMonster]);
        } else {
          setOpponentField(prev => prev.map((m, idx) => {
            if (idx === targetIndex && m) {
              return { ...m, currentHp: newHp };
            }
            return m;
          }));
        }
      };

      // 1体のみの場合は自動選択
      if (validTargets.length === 1) {
        applyDamage(validTargets[0].index);
        return true;
      }

      // 複数いる場合は選択UI
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: '1000ダメージを与える相手モンスターを選択',
          targetPlayer: 'opponent',
          callback: (selectedIndex: number) => {
            applyDamage(selectedIndex);
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      // フォールバック
      applyDamage(validTargets[0].index);
      return true;
    }

    return false;
  },

  /**
   * C0000398: 呪術狩りの呪焔術師ガルドリック
   * 【召喚時】自分の墓地のコスト3の魔法カード1枚を手札に戻す
   * 基本技: 自分のライフを500減らし、相手の闇属性モンスター1体に1200ダメージ
   *         （闇以外なら600ダメージ、ターンに1度）
   */
  C0000398: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { setMyLife, opponentField, setOpponentField, setOpponentGraveyard, myGraveyard, setMyGraveyard, setMyHand } = getPlayerContext(context);

    // 【召喚時】墓地のコスト3の魔法カード1枚を手札に戻す
    if (skillText.includes('【召喚時】')) {
      const targetCard = myGraveyard.find(card =>
        card.type === 'magic' && card.cost === 3
      );

      if (targetCard) {
        setMyGraveyard(prev => prev.filter(c => c.uniqueId !== targetCard.uniqueId));
        setMyHand(prev => [...prev, targetCard]);
        addLog(`墓地から「${targetCard.name}」を手札に戻した`, 'info');
        return true;
      } else {
        addLog('墓地にコスト3の魔法カードがありません', 'info');
        return false;
      }
    }

    if (context.skillType === 'basic') {
      // 相手モンスターがいるか確認
      const opponentMonsters = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster !== null);

      if (opponentMonsters.length === 0) {
        addLog('呪術狩りの呪焔術師ガルドリックの基本技: 相手モンスターがいません', 'info');
        return false;
      }

      const applyEffect = (targetIndex: number) => {
        // ライフを500減らす
        setMyLife(prev => Math.max(0, prev - 500));
        addLog('呪術狩りの呪焔術師ガルドリック: ライフを500消費', 'damage');

        // ダメージ計算（闇属性なら1200、それ以外なら600）
        const target = opponentField[targetIndex];
        if (!target) return;
        const isDark = target.attribute === '闇';
        const damage = isDark ? 1200 : 600;

        const newHp = target.currentHp - damage;
        addLog(`呪術狩りの呪焔術師ガルドリックの基本技: ${target.name}に${damage}ダメージ！${isDark ? '（闇属性）' : ''}`, 'damage');

        if (newHp <= 0) {
          setOpponentField(prev => {
            const newField = [...prev];
            newField[targetIndex] = null;
            return newField;
          });
          setOpponentGraveyard(prev => [...prev, target as unknown as CardWithId]);
          addLog(`${target.name}は破壊された！`, 'damage');
        } else {
          setOpponentField(prev => {
            const newField = [...prev];
            newField[targetIndex] = { ...target, currentHp: newHp };
            return newField;
          });
        }
      };

      // 1体のみの場合は自動選択
      if (opponentMonsters.length === 1) {
        applyEffect(opponentMonsters[0].index);
        return true;
      }

      // 複数いる場合は選択UI
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: 'ダメージを与える相手モンスターを選択',
          targetPlayer: 'opponent',
          callback: (targetIndex: number) => {
            applyEffect(targetIndex);
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      applyEffect(opponentMonsters[0].index);
      return true;
    }

    return false;
  },

  /**
   * C0000406: 血炎の精霊ブラッドフェニクス
   * 基本技: 自分のデッキの上から1枚を墓地に送り、相手モンスター1体に500ダメージ（ターンに1度）
   */
  C0000406: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myDeck, setMyDeck, setMyGraveyard, opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // デッキがあるか確認
      if (myDeck.length === 0) {
        addLog('血炎の精霊ブラッドフェニクスの基本技: デッキがありません', 'info');
        return false;
      }

      // 相手モンスターがいるか確認
      const opponentMonsters = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster !== null);

      if (opponentMonsters.length === 0) {
        addLog('血炎の精霊ブラッドフェニクスの基本技: 相手モンスターがいません', 'info');
        return false;
      }

      const applyEffect = (targetIndex: number) => {
        // デッキの上から1枚を墓地に送る
        const topCard = myDeck[0];
        setMyDeck(prev => prev.slice(1));
        setMyGraveyard(prev => [...prev, topCard]);
        addLog(`血炎の精霊ブラッドフェニクス: ${topCard.name}を墓地に送った`, 'info');

        // 相手モンスターに500ダメージ
        const target = opponentField[targetIndex];
        if (!target) return;
        const newHp = target.currentHp - 500;
        addLog(`血炎の精霊ブラッドフェニクスの基本技: ${target.name}に500ダメージ！`, 'damage');

        if (newHp <= 0) {
          setOpponentField(prev => {
            const newField = [...prev];
            newField[targetIndex] = null;
            return newField;
          });
          setOpponentGraveyard(prev => [...prev, target]);
          addLog(`${target.name}は破壊された！`, 'damage');
        } else {
          setOpponentField(prev => {
            const newField = [...prev];
            newField[targetIndex] = { ...target, currentHp: newHp };
            return newField;
          });
        }
      };

      // 1体のみの場合は自動選択
      if (opponentMonsters.length === 1) {
        applyEffect(opponentMonsters[0].index);
        return true;
      }

      // 複数いる場合は選択UI
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: '500ダメージを与える相手モンスターを選択',
          targetPlayer: 'opponent',
          callback: (targetIndex: number) => {
            applyEffect(targetIndex);
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      applyEffect(opponentMonsters[0].index);
      return true;
    }

    return false;
  },

  /**
   * C0000410: 峠の暴君ヴェルミグノス
   * 基本技: 自分のHPを600減らし、相手モンスター1体に1400ダメージ（ターンに1度）
   */
  C0000410: (skillText, context) => {
    const { addLog, monsterIndex, setPendingMonsterTarget } = context;
    const { myField, setMyField, opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // 自分のモンスターを取得
      const myMonster = myField[monsterIndex];
      if (!myMonster) {
        addLog('峠の暴君ヴェルミグノスの基本技: 自分のモンスターが見つかりません', 'info');
        return false;
      }

      // 相手モンスターがいるか確認
      const opponentMonsters = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster !== null);

      if (opponentMonsters.length === 0) {
        addLog('峠の暴君ヴェルミグノスの基本技: 相手モンスターがいません', 'info');
        return false;
      }

      const applyEffect = (targetIndex: number) => {
        // 自分のHPを600減らす
        setMyField(prev => {
          const newField = [...prev];
          if (newField[monsterIndex]) {
            const newHp = newField[monsterIndex].currentHp - 600;
            if (newHp <= 0) {
              addLog('峠の暴君ヴェルミグノス: 自分のHPが0になり自壊！', 'damage');
              // 自壊処理（墓地送りはトリガーで処理される想定）
              newField[monsterIndex] = { ...newField[monsterIndex], currentHp: 0 };
            } else {
              newField[monsterIndex] = { ...newField[monsterIndex], currentHp: newHp };
            }
          }
          return newField;
        });
        addLog('峠の暴君ヴェルミグノス: 自分のHPを600消費', 'damage');

        // 相手モンスターに1400ダメージ
        const target = opponentField[targetIndex];
        if (!target) return;
        const newHp = target.currentHp - 1400;
        addLog(`峠の暴君ヴェルミグノスの基本技: ${target.name}に1400ダメージ！`, 'damage');

        if (newHp <= 0) {
          setOpponentField(prev => {
            const newField = [...prev];
            newField[targetIndex] = null;
            return newField;
          });
          setOpponentGraveyard(prev => [...prev, target]);
          addLog(`${target.name}は破壊された！`, 'damage');
        } else {
          setOpponentField(prev => {
            const newField = [...prev];
            newField[targetIndex] = { ...target, currentHp: newHp };
            return newField;
          });
        }
      };

      // 1体のみの場合は自動選択
      if (opponentMonsters.length === 1) {
        applyEffect(opponentMonsters[0].index);
        return true;
      }

      // 複数いる場合は選択UI
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: '1400ダメージを与える相手モンスターを選択',
          targetPlayer: 'opponent',
          callback: (targetIndex: number) => {
            applyEffect(targetIndex);
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      applyEffect(opponentMonsters[0].index);
      return true;
    }

    return false;
  },

  /**
   * C0000428: 血焔の嵐
   * 相手フィールド全体に800ダメージを与える。自分のライフが3000以下の場合、このダメージを1200に変更。
   */
  C0000428: (skillText, context) => {
    const { addLog } = context;
    const { myLife, opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    // 自分のライフが3000以下なら1200、それ以外は800
    const damage = myLife <= 3000 ? 1200 : 800;

    addLog(`血焔の嵐発動！相手フィールド全体に${damage}ダメージ！${myLife <= 3000 ? '（ライフ3000以下で威力上昇）' : ''}`, 'damage');

    const destroyedMonsters: MonsterInstance[] = [];

    setOpponentField((prev) => {
      return prev.map((monster) => {
        if (monster) {
          const newHp = monster.currentHp - damage;
          if (newHp <= 0) {
            destroyedMonsters.push(monster);
            addLog(`${monster.name}は破壊された！`, 'damage');
            return null;
          }
          return { ...monster, currentHp: newHp };
        }
        return monster;
      });
    });

    // 破壊されたモンスターを墓地に送る
    if (destroyedMonsters.length > 0) {
      setOpponentGraveyard((prev) => [...prev, ...destroyedMonsters as unknown as CardWithId[]]);
    }

    return true;
  },

  // ========================================
  // 彷徨い状態解除カード
  // ========================================

  /**
   * C0000573: 魂呼びの聖火巫女
   * 基本技: 手札に「聖火の救済」を生成する。
   */
  C0000573: (skillText, context) => {
    const { addLog } = context;
    const { setMyHand } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // 「聖火の救済」カードを生成
      const seikaCard: CardWithId = {
        id: 'C0000574',
        name: '聖火の救済',
        attribute: '光',
        cost: 0,
        type: 'magic',
        effect: '自分のモンスター1体の状態異常を回復し、HPを500回復。',
        uniqueId: `C0000574_generated_${Date.now()}_${Math.random()}`,
      };

      setMyHand((prev) => [...prev, seikaCard]);
      addLog('魂呼びの聖火巫女の基本技: 「聖火の救済」を手札に生成！', 'info');
      return true;
    }

    return false;
  },
};
